<template>
  <div class="container-fluid">
    <b-row class=" no-gutters justify-content-around my-5">
      <b-col cols="12" lg="6" offset-xl="1" xl="6" class="mb-3">
        <div class="mb-5">
          <h5>
            {{ title }}
          </h5>
        </div>
        <UsersForm />
        <div>
          <h5>Groupe(s)</h5>
          <span
            >Les groupes permettent de rassembler les étudiants pour faciliter
            leur évaluation.</span
          >
          <b-table
            show-empty
            bordered
            small
            stacked="md"
            :items="groups.data.data"
            :fields="reportFields"
            class="mt-5"
            empty-text="Il n'y a pas de groupes correspondant à vos critères de recherche"
          >
            <template v-slot:cell(list_Participant)="row">
              <b-button
                variant="link"
                :disabled="row.item.total_participants < 1"
                :to="{
                  name: 'admin.users.per.group',
                  params: { id: row.item.id }
                }"
              >
                liste
              </b-button>
            </template>
          </b-table>
          <b-pagination
            pills
            v-model="table.currentPage"
            :total-rows="table.totalRows"
            :per-page="table.perPage"
            class="my-0 mb-5"
            @change="pageChange"
          ></b-pagination>
        </div>
      </b-col>
      <b-col
        cols="12"
        lg="5"
        offset-xl="1"
        xl="4"
        class="pl-5 border-left border-gray"
      >
        <b-row class="mb-4">
          <b-col cols="12" offset-sm="5" sm="7">
            <b-btn
              v-if="profile.status !== 2"
              :disabled="callProfile == 'fetching'"
              class="text-danger"
              variant="link"
              @click="
                changeStatus({
                  user_id: profile.id,
                  status: status.value
                })
              "
              >{{ status.statusButton }}</b-btn
            >
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col cols="12" sm="5">
            <h5>
              Statut
            </h5>
          </b-col>
          <b-col cols="12" sm="7" class="pl-4 pl-sm-3">
            {{ status.status }}
          </b-col>
        </b-row>
        <b-row class="mb-5">
          <b-col cols="12" sm="5">
            {{ status.adateLabel }}
          </b-col>
          <b-col cols="12" sm="7" class="pl-4 pl-sm-3">
            {{ status.date }}
          </b-col>
        </b-row>
        <b-row>
          <b-col xs="12">
            <b-btn
              v-if="profile.status == 1 || profile.status == 2"
              class="text-white"
              pill
              variant="info"
              :disabled="callSendInvitation == 'fetching'"
              @click="
                sendInvitation({
                  user: profile.id
                })
              "
            >
              <span> {{ status.inviteButton }}</span>
              <b-spinner
                class="ml-2"
                small
                v-if="callSendInvitation == 'fetching'"
              ></b-spinner>
            </b-btn>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/**
 * @file       Users profile component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import store from "@/store";
import UsersForm from "@/modules/admin/components/usersForm.vue";
import { mapState } from "vuex";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  components: { UsersForm },

  /**
   * Before route change.
   * 
   * @param  {string}   to     Destination
   * @param  {string}   from   Location
   * @param  {Object}   next   The action
   */
  beforeRouteEnter(to, from, next) {
    store.commit("groups/setTeacherGroupsFilters", {
      reset: true,
      fields: {
        teacher: parseInt(to.params.id),
        status: 1
      }
    });
    store.dispatch("groups/getTeacherGroups").then(() => {
      if (from.name !== "admin.users") {
        store.dispatch("admin/getUserByID", to.params.id).then(() => next());
      } else {
        next();
      }
    });
  },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      title: "Profil de l'enseignant",
      status: {
        status: null,
        adateLabel: null,
        date: null,
        inviteButton: null,
        statusButton: null,
        value: 1
      },
      reportFields: [
        {
          key: "name",
          label: "Nom du groupe"
        },
        {
          key: "total_participants",
          label: "Nombre de participants",
          class: "text-center"
        },
        {
          key: "list_Participant",
          label: "Voir la liste des participants",
          class: "text-center"
        }
      ]
    };
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      profile: state => state.admin.user.data,
      callProfile: state => state.admin.user.status,
      callSendInvitation: state => state.admin.sendInvitation.status,
      groups: state => state.groups.teacherGroups
    }),

    /**
     * Pager table.
     *
     * @return     {Object}
     */
    table() {
      return {
        totalRows: this.groups.data.total,
        currentPage: this.groups.data.current_page,
        perPage: this.groups.data.per_page
      };
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Change teacher groups page.
     *
     * @param      {int}  page     The page number
     */
    pageChange(page) {
      store.commit("groups/setTeacherGroupsFilters", {
        fields: { page }
      });
      store.dispatch("groups/getTeacherGroups");
    },

    /**
     * Change user status.
     *
     * @param      {Object}  payload     The user
     */
    changeStatus(payload) {
      store
        .dispatch("admin/putUser", payload)
        .then(() => {
          this.$root.$bvToast.toast("La requête a été envoyé avec succès.", {
            title: "Succès"
          });
        })
        .catch(() => {
          this.$root.$bvToast.toast("La requête a échoué", {
            title: "Erreur",
            variant: "danger"
          });
        });
    },

    /**
     * Send user invitation.
     *
     * @param      {Object}  payload     The user
     */
    sendInvitation(payload) {
      store
        .dispatch("admin/postSendInvitation", payload)
        .then(() => {
          this.$root.$bvToast.toast("L'invitaion a été envoyé avec succès.", {
            title: "Succès"
          });
          store.dispatch("admin/getUserByID", this.profile.id);
        })
        .catch(() => {
          this.$root.$bvToast.toast("L'invitaion a échoué.", {
            title: "Erreur",
            variant: "danger"
          });
        });
    },

    /**
     * Format user status.
     *
     * @param      {int}  status     The status
     *
     * @return     {String}          The status display text
     */
    statusFormat(status) {
      switch (parseInt(status)) {
        case 0:
          this.status.status = "inactif";
          this.status.adateLabel = "Date d'activation";
          this.status.date = this.$options.filters.formatDateTime(
            this.profile.activation_date
          );
          this.status.statusButton = "Changer pour Actif";
          this.status.value = 3;
          break;
        case 1:
          this.status.status = "Nouveau applicant";
          this.status.adateLabel = "Date de soumission";
          this.status.date = this.$options.filters.formatDateTime(
            this.profile.submit_date
          );
          this.status.inviteButton = "Envoyer une invitation";
          this.status.statusButton = "Archiver cette utilisateur";
          this.status.value = 4;
          break;
        case 2:
          this.status.status = "Invité";
          this.status.adateLabel = "Invitation envoyée";
          this.status.date = this.$options.filters.formatDateTime(
            this.profile.invitation_date
          );
          this.status.inviteButton = "Envoyer l'invitation de nouveau";
          break;
        case 3:
          this.status.status = "Actif";
          this.status.adateLabel = "Date d'activation";
          this.status.date = this.$options.filters.formatDateTime(
            this.profile.activation_date
          );
          this.status.statusButton = "Changer pour inactif";
          this.status.value = 0;
          break;
        case 4:
          this.status.status = "Archiver";
          this.status.adateLabel = "Date de soumission";
          this.status.date = this.$options.filters.formatDateTime(
            this.profile.submit_date
          );
          this.status.statusButton = "Désarchiver cette utilisateur";
          this.status.value = 1;
          break;
      }
    }
  },
  watch: {
    profile: function(data) {
      this.statusFormat(data.status);
    }
  },
  mounted() {
    if (this.profile.role === "admin") {
      this.title = "Profil de l'administrateur";
    }
    this.statusFormat(this.profile.status);
  }
};
</script>

<style lang="scss" scoped></style>
